import { Component, EventEmitter, HostListener, OnInit, Output, ChangeDetectionStrategy } from '@angular/core'
import { ApiService } from 'src/app/services/api.service'
import { UiService } from 'src/app/services/ui.service'
import { AccountService } from 'src/app/account/services/account.service'
import { Application, ApplicationProviderService } from 'src/app/account/services/application.service'
import { LaunchSyncDialogBase } from 'src/app/account/components/launch-sync-dialog-base'
import { AccessScope, AccessService } from 'src/app/services/access.service'
import { AccessTemplatesDto, SyncPairInfo, SyncPhaseDto } from 'src/app/services/dtos'
import { finalize, switchMap } from 'rxjs/operators'
import { of, Subscription } from 'rxjs'
import { handleArpioError } from 'src/utils/handle-error'
import { subscriptionActive } from 'src/utils/rxjs-utils'
import { AccessLevelService, AppPermission } from 'src/app/services/access-level.service'

export interface FailbackSettings {
  applications: Array<Application>
}

@Component({
  // tslint:disable-next-line:component-selector
  selector: 'failback-dialog',
  templateUrl: './failback-dialog.component.html',
  styleUrls: ['./failback-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class FailbackDialogComponent extends LaunchSyncDialogBase implements OnInit {
  Scope = AccessScope
  failbackAccessService: AccessService = null
  private initialValidationSubscription: Subscription = null
  public failbackAccessInitiallyAllGood = true

  constructor(
    applicationProviderService: ApplicationProviderService,
    accountService: AccountService,
    apiService: ApiService,
    uiService: UiService,
    accessLevelService: AccessLevelService
  ) {
    super(applicationProviderService, accountService, apiService, uiService, accessLevelService)
  }

  @Output() failback = new EventEmitter<FailbackSettings>()
  failbackAccessTemplate = null

  // Esc = Cancel. Listens at the document level so focus position inside the
  // modal is irrelevant. Short-circuits during an in-flight failback so an
  // accidental Esc can't tear down the dialog mid-request.
  @HostListener('document:keydown.escape', ['$event'])
  onEscapeKeydown(event: KeyboardEvent): void {
    if (!this.isTopmostModal()) return
    if (this.launching) return
    event.preventDefault()
    this.onCancelClick()
  }

  ngOnInit() {
    super.ngOnInit()
    const buildAccountValidationObservable = () => {
      // compose an observable that will query for access, and if not accessible will
      // query for access templates and set the variables for them.
      const requestDto = this.application as SyncPairInfo
      return this.apiService.testSingleSyncPairAccess(requestDto, AccessScope.FAILBACK).pipe(
        switchMap((awsAccess) => {
          this.failbackAccessService = new AccessService(
            this.apiService,
            this.uiService,
            requestDto,
            AccessScope.FAILBACK,
            awsAccess,
            'failback-dialog'
          )
          // Mirror AccessService.allGood so the "right access already" banner and the
          // FAILBACK button's disabled gate can't disagree — previously this flag omitted
          // configValid, leaving the banner green while the button stayed disabled.
          this.failbackAccessInitiallyAllGood = this.failbackAccessService.allGood
          if (this.failbackAccessService.allGood) {
            return of(null as AccessTemplatesDto)
          }
          return this.apiService.getAccessTemplates(
            this.application.accountId,
            this.application.sourceEndpointUri,
            this.application.targetEndpointUri
          )
        })
      )
    }
    const initialObservable = buildAccountValidationObservable()
    this.initialValidationSubscription = initialObservable
      .pipe(
        finalize(() => {
          this.initialValidationSubscription = null
          this.cdr.markForCheck()
        })
      )
      .subscribe(
        (templatesDto: AccessTemplatesDto) => {
          if (templatesDto) {
            this.failbackAccessTemplate = templatesDto.failbackTemplateS3Url
          }
        },
        handleArpioError(window.location.href, this.apiService, this.uiService, {
          errorContext: { dialogId: 'failback-dialog' },
        }),
        () => {}
      )
  }

  protected applicationIsCandidate(application: Application) {
    return application.syncPhase === SyncPhaseDto.FAILOVER
  }

  get loading(): boolean {
    return subscriptionActive(this.initialValidationSubscription)
  }

  onFailbackClick() {
    this.failback.emit({ applications: Array.from(this.selectedApplications) } as FailbackSettings)
  }

  get appName(): string {
    return !!this.application ? this.application.name : ''
  }

  get notReadyToFailback(): boolean {
    return (
      this.selectedApplications.length === 0 ||
      this.failbackAccessService === null ||
      !this.failbackAccessService.allGood
    )
  }
  get failbackDocsUrl(): string {
    return this.uiService.failbackDocsUrl
  }

  protected getRequiredPermission(): AppPermission {
    return AppPermission.MANAGE_RECOVERY
  }
}
